import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Manager-configurable named time windows with required headcount per
// location. Replace-all save semantics — simpler than diffing for a
// 4-period list. Future round will surface these in the Availability
// Health chart so a 2-person shift renders green during Post-Peak but
// red during Peak.
export function StaffingPeriodsEditor({ locationId, currentUser, canEdit }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMsg, setSavingMsg] = useState('');

  async function load() {
    if (!locationId) return;
    setLoading(true);
    try {
      const data = await api('GET', `/api/locations/${locationId}/staffing-periods`, null, headers);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { setSavingMsg(`Load failed: ${e.message || e}`); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [locationId]);

  const DOW_LIST = ['mon','tue','wed','thu','fri','sat','sun'];
  const DOW_LABEL = { mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat', sun:'Sun' };

  function parseDows(val) {
    if (Array.isArray(val)) return val.map(d => String(d).toLowerCase()).filter(d => DOW_LIST.includes(d));
    if (typeof val === 'string' && val) {
      try { const a = JSON.parse(val); return Array.isArray(a) ? a.map(d => String(d).toLowerCase()).filter(d => DOW_LIST.includes(d)) : []; } catch { return []; }
    }
    return [];
  }
  function dateInput(val) {
    if (!val) return '';
    if (typeof val === 'string') return val.slice(0, 10);
    try { return new Date(val).toISOString().slice(0, 10); } catch { return ''; }
  }
  function update(idx, field, val) {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  }
  function toggleDow(idx, dow) {
    setRows(rs => rs.map((r, i) => {
      if (i !== idx) return r;
      const cur = parseDows(r.days_of_week);
      const next = cur.includes(dow) ? cur.filter(d => d !== dow) : [...cur, dow];
      return { ...r, days_of_week: next };
    }));
  }
  function addRow() {
    setRows(rs => [...rs, { name: '', start_time: '06:00', end_time: '09:00', required_staff: 2, sort_order: rs.length, days_of_week: [], effective_start_date: null, effective_end_date: null }]);
  }
  function removeRow(idx) {
    setRows(rs => rs.filter((_, i) => i !== idx));
  }
  function presetFour() {
    if (rows.length > 0 && !confirm('Replace existing periods with the default 4 (Open / Peak / Post-Peak / Close)?')) return;
    setRows([
      { name: 'Open',      start_time: '05:30', end_time: '07:30', required_staff: 2, sort_order: 0, days_of_week: [], effective_start_date: null, effective_end_date: null },
      { name: 'Peak',      start_time: '07:30', end_time: '11:00', required_staff: 4, sort_order: 1, days_of_week: [], effective_start_date: null, effective_end_date: null },
      { name: 'Post-Peak', start_time: '11:00', end_time: '15:00', required_staff: 2, sort_order: 2, days_of_week: [], effective_start_date: null, effective_end_date: null },
      { name: 'Close',     start_time: '15:00', end_time: '19:00', required_staff: 2, sort_order: 3, days_of_week: [], effective_start_date: null, effective_end_date: null },
    ]);
  }
  async function save() {
    setSavingMsg('Saving…');
    try {
      const periods = rows.map((r, i) => ({
        name: r.name,
        start_time: normalizeHHMM(r.start_time),
        end_time: normalizeHHMM(r.end_time),
        required_staff: Number(r.required_staff) || 0,
        sort_order: i,
        days_of_week: parseDows(r.days_of_week),
        effective_start_date: dateInput(r.effective_start_date) || null,
        effective_end_date: dateInput(r.effective_end_date) || null,
      }));
      await api('PUT', `/api/locations/${locationId}/staffing-periods`, { periods }, headers);
      setSavingMsg('Saved.');
      load();
    } catch (e) { setSavingMsg(`Save failed: ${e.message || e}`); }
  }

  function normalizeHHMM(s) {
    if (!s) return '00:00:00';
    const m = String(s).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return '00:00:00';
    return `${m[1].padStart(2,'0')}:${m[2]}:${m[3] || '00'}`;
  }
  function hhmmInput(s) {
    if (!s) return '';
    return String(s).slice(0, 5); // strip seconds for the <input type=time>
  }

  const [showHelp, setShowHelp] = useState(false);
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div>
          <div className="text-sm font-semibold" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Staffing periods
            <button type="button" onClick={() => setShowHelp(s => !s)}
              title="What does this do?"
              style={{ width: 18, height: 18, borderRadius: '50%', border: '1px solid #d0d4d9',
                        background: '#fff', color: '#666', fontSize: 11, fontWeight: 700,
                        cursor: 'pointer', padding: 0, lineHeight: '16px' }}>i</button>
          </div>
          <div className="text-xs text-gray-500">Named time windows that drive Availability Health. Each period applies on the selected days within an optional date range.</div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={presetFour} className="btn-secondary" style={{ fontSize: 11 }}>Use 4-period default</button>
            <button onClick={addRow} className="btn-secondary" style={{ fontSize: 11 }}>+ Add period</button>
            <button onClick={save}
              style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Save
            </button>
          </div>
        )}
      </div>
      {showHelp && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fef9ee',
                        border: '1px solid #f5e6c8', borderRadius: 8, fontSize: 12, color: '#5a4220' }}>
          <strong>How staffing periods work:</strong> a period is a named time window (e.g. <em>Open</em>, <em>Peak</em>, <em>Close</em>) plus the days of the week it applies on. The Availability Health view counts how many staff are scheduled to be available during each period and flags shortages. <strong>Selecting a day</strong> means "this period is active on that day" — for example, if Peak is Mon–Fri only, leave Sat + Sun unchecked. Optional date ranges let you run different period sets per season (e.g. summer hours).
        </div>
      )}
      {loading ? (
        <div style={{ fontSize: 12, color: '#9e9e9e' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9e9e9e' }}>No periods configured. Click "Use 4-period default" or add rows manually.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r, i) => {
            const dows = parseDows(r.days_of_week);
            return (
              <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 10, background: '#fafafa' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 90px 32px', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                  <input value={r.name || ''} onChange={e => update(i, 'name', e.target.value)} placeholder="e.g., Peak" disabled={!canEdit}
                    style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 4 }} />
                  <input type="time" value={hhmmInput(r.start_time)} onChange={e => update(i, 'start_time', e.target.value)} disabled={!canEdit}
                    style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 4 }} />
                  <input type="time" value={hhmmInput(r.end_time)} onChange={e => update(i, 'end_time', e.target.value)} disabled={!canEdit}
                    style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 4 }} />
                  <input type="number" min="0" value={r.required_staff ?? 0} onChange={e => update(i, 'required_staff', e.target.value)} disabled={!canEdit}
                    style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 4 }} />
                  {canEdit && (
                    <button onClick={() => removeRow(i)}
                      style={{ background: '#fff', border: '1px solid #d0d4d9', borderRadius: 4, color: '#dc2626', cursor: 'pointer', fontSize: 16, lineHeight: 1, height: 28 }}>×</button>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', fontSize: 12 }}>
                  <div>
                    <span style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginRight: 6 }}>Days</span>
                    {DOW_LIST.map(d => {
                      const on = dows.includes(d);
                      return (
                        <button key={d} type="button" disabled={!canEdit}
                          onClick={() => toggleDow(i, d)}
                          style={{ marginRight: 3, padding: '3px 7px', fontSize: 11, fontWeight: 600,
                                    background: on ? 'var(--forest)' : '#fff',
                                    color: on ? '#fff' : '#37474f',
                                    border: `1px solid ${on ? 'var(--forest)' : '#d0d4d9'}`,
                                    borderRadius: 4, cursor: canEdit ? 'pointer' : 'default' }}>
                          {DOW_LABEL[d]}
                        </button>
                      );
                    })}
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#9e9e9e' }}>
                      {dows.length === 0 ? '(every day)' : `${dows.length} day${dows.length === 1 ? '' : 's'}`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Effective</span>
                    <input type="date" value={dateInput(r.effective_start_date)} onChange={e => update(i, 'effective_start_date', e.target.value || null)} disabled={!canEdit}
                      style={{ padding: '3px 6px', fontSize: 12, border: '1px solid #d0d4d9', borderRadius: 4 }} />
                    <span style={{ fontSize: 11, color: '#9e9e9e' }}>→</span>
                    <input type="date" value={dateInput(r.effective_end_date)} onChange={e => update(i, 'effective_end_date', e.target.value || null)} disabled={!canEdit}
                      style={{ padding: '3px 6px', fontSize: 12, border: '1px solid #d0d4d9', borderRadius: 4 }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {savingMsg && (
        <div style={{ fontSize: 11, color: savingMsg.includes('failed') ? '#dc2626' : '#16a34a', marginTop: 6 }}>{savingMsg}</div>
      )}
    </div>
  );
}
